import { CreateEstimationContext } from "@/context/create-estimation-context";
import { CreateEstimationProvider } from "@/context/CreateEstimationProvider";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { dispatch, useSelector } from "@/redux/store";
import { estimationActions } from "@/redux/actions/estimation.actions";
import type { EstimateUploadState } from "../estimation-details/upload/UploadEstimationFiles";
import { UploadEstimationData } from "../estimation-details/upload/UploadEstimationFiles";
import { v4 as uuidv4 } from "uuid";
import type { Estimation } from "@/interfaces/estimation.interface";
import { ScopeNoteSource } from "@/interfaces/estimation.interface";
import { EstimateCreationSource, EstimateStatus, getNoteTypeFromEstimateType } from "@rebuild/shared";
import { appSelector } from "@/redux/selectors/app.selector";
import estimateProvider from "@/providers/estimate.provider";
import { toast } from "sonner";
import { useIsMobile } from "@/hooks/useIsMobile";
import { EstimateType } from "@/enums/estimate-type";
import type { AppState } from "@/redux/rootReducer";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { useQueryClient } from "@tanstack/react-query";
import { JOB_KEYS } from "@/hooks/queries/jobs/queryKeys";
import {
  clearScopeNoteDraft,
  NEW_ESTIMATE_SCOPE_NOTE_DRAFT_ID,
} from "@/components/estimation-details/upload/scopeNoteDraftStorage";

export const Layout = () => {
  return (
    <div className="absolute inset-0 flex flex-col">
      <div className="flex min-h-0 flex-1 flex-row">
        <CreateEstimationProvider>
          <AppContent />
        </CreateEstimationProvider>
      </div>
    </div>
  );
};

const AppContent = () => {
  const context = useContext(CreateEstimationContext);
  const user = useSelector(appSelector.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [estimateToSubmit, setEstimateToSubmit] = useState<Estimation>();

  const userId = useSelector(appSelector.user)?.lassoUserId;
  const organizationId = useSelector(appSelector.organizationId);

  const { isFeatureEnabled } = useFeatureFlags();
  const mitigationEnabled = isFeatureEnabled("MITIGATION_ENABLED");

  const isMobile = useIsMobile();

  const estimateId = useMemo(() => uuidv4(), [context?.showNewEstimationModal]);

  // Get conversation context to access projectAddress
  const conversationContext = useSelector((state: AppState) => state.estimation.conversationContext?.[estimateId || ""]);
  useEffect(
    () =>
      setEstimateToSubmit({
        id: estimateId,
        autoMergeRoomCompleted: false,
        dateCreated: new Date().toLocaleDateString("en-US"),
        estimateId,
        projectName: "",
        assignee: user?.lassoUserId ?? "",
        estimateReferenceNumber: "",
        requesterName: "",
        email: "",
        phone: "",
        propertyAddress: "",
        insuranceCarrier: "",
        claimNumber: "",
        tpa: "",
        dateContacted: "",
        dateOfLoss: "",
        office: "",
        approximateClaim: "",
        links: [],
        notes: [],
        files: [],
        fieldData: "",
        status: EstimateStatus.RECEIVED,
        category: "",
        typeOfLoss: "",
        deleted: false,
        measurementData: [],
        propertyAddressComponents: "",
        uploadedFilesProperties: [],
        conversationIds: [],
        // If Mitigation is enabled, we default to Mitigation and provide a toggle in the UI for users to switch the Estimate type.
        // If Mitigation is not enabled, we default to Recon if Desktop and Mitigation if mobile
        // (this is a short term condition until we can move all organizations to Mitigation enabled but can still allow organizations to use Reese on mobile)
        estimateType: mitigationEnabled ? EstimateType.MITIGATION : isMobile ? EstimateType.MITIGATION : EstimateType.RECONSTRUCTION,
        noteType: getNoteTypeFromEstimateType(
          mitigationEnabled ? EstimateType.MITIGATION : isMobile ? EstimateType.MITIGATION : EstimateType.RECONSTRUCTION,
        ),
        creationSource: EstimateCreationSource.REBUILD_WEB,
      }),
    [estimateId, mitigationEnabled, user?.lassoUserId],
  );

  /**
   * Called when creating a new project.
   */
  const createEstimaticsEstimate = useCallback(
    async (estimate: Estimation) => {
      dispatch(
        estimationActions.setEstimationState({
          isLoading: true,
        }),
      );

      try {
        if (estimate?.projectName === "") {
          estimate.projectName = generateProjectName();
        }
        const response = await estimateProvider.createEstimaticsEstimate({
          estimaticsData: estimate!,
          scopeNoteUploadEnabled: true, // HACK for right now to allow Reese to upload scope notes for Tic-Sheet organizations
          ...(conversationContext?.projectAddress && {
            addressQuery: conversationContext?.projectAddress,
          }),
        });
        const createdEstimation = response.estimaticsData;
        if (!createdEstimation) {
          throw new Error(`Doesn't look like the backend returned the created estimation object`);
        }

        // to prevent detail empty screen
        await new Promise((resolve) => setTimeout(resolve, 1000));

        if (!response.success) {
          toast.error("Error creating estimate", {
            className: "!text-red-500 !bg-red-500/10 !border-red-500",
          });
          return;
        }

        dispatch(
          estimationActions.setEstimationState({
            uploadedFilesPropertiesCreated: createdEstimation?.uploadedFilesProperties,
            files: createdEstimation.files,
          }),
        );

        queryClient.invalidateQueries({
          queryKey: JOB_KEYS.forEstimate(estimate.estimateId),
        });

        clearScopeNoteDraft({
          userId,
          organizationId,
          draftId: NEW_ESTIMATE_SCOPE_NOTE_DRAFT_ID,
        });

        navigate(`/estimate/${estimate?.estimateId}`);
        context?.toggleShowNewEstimationModal();
      } catch {
        toast.error("Error creating project. Our team has been notified.", {
          className: "!text-red-500 !bg-red-500/10 !border-red-500",
        });
      } finally {
        dispatch(
          estimationActions.setEstimationState({
            isLoading: false,
            estimationCreated: undefined,
          }),
        );
      }
    },
    [navigate, context, conversationContext?.projectAddress, queryClient, userId, organizationId],
  );

  const onSubmit = async (uploadData: EstimateUploadState) => {
    const { scopeNoteText, scopeNoteWorkAfterHours, link, ...estimationData } = uploadData;

    if (estimateToSubmit === undefined) {
      return;
    }

    const _estimateToSubmit: Estimation = {
      ...estimateToSubmit,
      ...estimationData,
    };

    if (link) {
      _estimateToSubmit.links ??= [];
      _estimateToSubmit.links = [..._estimateToSubmit.links, link];
    }
    if (scopeNoteText) {
      _estimateToSubmit.scopeNoteObjects ??= [];
      _estimateToSubmit.scopeNoteObjects = [
        ..._estimateToSubmit.scopeNoteObjects,
        {
          source: ScopeNoteSource.UPLOADED,
          uuid: uuidv4(),
          dateCreated: new Date().toISOString(),
          createdByUserId: userId,
          text: scopeNoteText,
          workAfterHours: scopeNoteWorkAfterHours,
        },
      ];
    }

    createEstimaticsEstimate(_estimateToSubmit);
    setEstimateToSubmit(_estimateToSubmit);
  };

  return (
    <div className={`relative rounded-lg bg-white shadow-lg ${!isMobile && "m-2 mr-2 mb-4 min-w-0 flex-1 overflow-hidden"}`}>
      {/*
        In index.tsx there are nested routes (e.g. for /dashboard and /analytics) that both render Layout.
        Within each of those they specify a sub-route with a page like <DashboardPage /> or <AnalyticsPage />.
        Those sub-route elements appear to get rendered when <Outlet /> is declared later. Disclaimer: I (Wade)
        don't fully understand this, but suspect this should be refactored to something simpler soon.
      */}
      <Outlet />

      <UploadEstimationData
        estimation={estimateToSubmit}
        onSubmit={onSubmit}
        onClose={() => context?.toggleShowNewEstimationModal()}
        submitText="Next"
        show={!!context?.showNewEstimationModal}
      />
    </div>
  );
};

export const generateProjectName = (): string => {
  const now = new Date();

  const month = now.getMonth() + 1;
  const day = now.getDate();
  const hours = now.getHours();
  const minutes = now.getMinutes();

  const nameSuffix = `${month}${day}${hours}${minutes}`;

  return `New Project ${nameSuffix}`;
};
